import { isMobileDevice } from '@va/dashboard/selectors/ui';
import { CogIconV2, HistoryIcon, MinusIcon, NotesIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { isUserLoggedInWithCustomUrl } from '@va/standalone/shared/helpers';
import { TwiplaLogo } from '@va/svg-visa-icons';
import {
  Button,
  fontWeights,
  IconWithBackground,
  Paragraph,
  TabbedButton,
  TabbedButtonProps,
} from '@va/ui/design-system';
import { useTooltipContext } from '@va/ui/tooltips';
import { is3asApp } from '@va/util/helpers';
import { findFirstFocusableElement, useIsolatedFocusedContainer } from '@va/util/misc';
import classNames from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AiChatbotView } from '../types';
import { AiChatbotProvider, useAiChatbotContext } from './aiChatbotContext';
import { ChatDetails } from './chatbot-tabs/chat-details/ChatDetails';
import { ChatsHistoryTab } from './chatbot-tabs/ChatsHistoryTab';
import { EditChatNameModal } from './chatbot-tabs/EditChatNameModal';
import { SettingsTab } from './chatbot-tabs/SettingsTab';
import { StartScreen } from './chatbot-tabs/StartScreen';

const AiChatbotContent = () => {
  const translate = useTranslate();
  const tooltip = useTooltipContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useSelector(isMobileDevice);
  const { selectedView, setSelectedView, onClose, setSelectedChat, isChatNameModalOpen } = useAiChatbotContext();

  useIsolatedFocusedContainer({
    containerRef,
    enabled: !!tooltip.context?.open,
    dependencies: [selectedView, isChatNameModalOpen],
    focusFirstElement: false,
  });

  useEffect(() => {
    if (!containerRef.current || !tooltip.context?.open || isChatNameModalOpen) return;

    const firstFocusable = findFirstFocusableElement(containerRef.current);
    firstFocusable?.focus();
  }, [isChatNameModalOpen, tooltip.context?.open]);

  const tabOptions = useMemo<TabbedButtonProps['buttonOptions']>(
    () => [
      {
        label: translate('aiChatbot.tabs.newChat'),
        value: AiChatbotView.NewChat,
        icon: (
          <NotesIcon
            className='w-10u h-10u'
            color={selectedView === AiChatbotView.NewChat ? `var(--color-primary)` : '#1A1A1A'}
          />
        ),
        iconPosition: 'start',
        onClick: () => {
          setSelectedChat(null);
        },
      },
      {
        label: translate('aiChatbot.tabs.chatsHistory'),
        value: AiChatbotView.ChatsHistory,
        icon: (
          <HistoryIcon
            className='w-10u h-10u'
            color={selectedView === AiChatbotView.ChatsHistory ? `var(--color-primary)` : '#1A1A1A'}
          />
        ),
        iconPosition: 'start',
      },
      {
        label: translate('aiChatbot.tabs.settings'),
        value: AiChatbotView.Settings,
        icon: (
          <CogIconV2
            className='w-10u h-10u'
            color={selectedView === AiChatbotView.Settings ? `var(--color-primary)` : '#1A1A1A'}
          />
        ),
        iconPosition: 'start',
      },
    ],
    [selectedView, setSelectedChat, translate],
  );

  return (
    <div
      ref={containerRef}
      className={classNames(
        'unit-scale rounded-bl-10u rounded-tl-20u overflow-hidden flex flex-col h-full transition-[width]',
        {
          'w-svw': isMobile,
          'w-108': selectedView !== AiChatbotView.ChatDetails && !isMobile,
          'w-[37.5rem]': selectedView === AiChatbotView.ChatDetails && !isMobile,
        },
      )}
    >
      <header className='bg-primary flex items-center text-white p-4u pr-8u gap-4u'>
        <IconWithBackground className='bg-black-lighter-25' icon={() => <Logo />} />
        <div>
          <Paragraph weight={fontWeights.semibold} className='text-interface-large leading-interface-large'>
            {translate('aiChatbot.header.title')}
          </Paragraph>
          <Paragraph weight={fontWeights.medium} className='text-interface-regular leading-interface-regular'>
            {translate('aiChatbot.header.subtitle')}
          </Paragraph>
        </div>
        <Button
          size='small'
          className='ml-auto bg-white-16'
          icon={() => <MinusIcon className='w-12u h-12u' color='#FFF' />}
          onClick={onClose}
        />
      </header>
      <div className='bg-gray-mercury-darker flex-1 flex flex-col relative overflow-hidden'>
        {selectedView !== AiChatbotView.Start && (
          <TabbedButton
            className='h-32u! p-4u! rounded-6u! sticky top-0 bg-transparent'
            textClassName='text-interface-regular leading-interface-regular'
            size='small'
            buttonOptions={tabOptions}
            onChange={setSelectedView}
            selectedValue={selectedView}
          />
        )}
        {selectedView === AiChatbotView.Start && <StartScreen />}
        {(selectedView === AiChatbotView.NewChat || selectedView === AiChatbotView.ChatDetails) && <ChatDetails />}
        {selectedView === AiChatbotView.ChatsHistory && <ChatsHistoryTab />}
        {selectedView === AiChatbotView.Settings && <SettingsTab />}
      </div>
    </div>
  );
};

type AiChatbotProps = {
  questionsCount?: number;
};

export const AiChatbot = ({ questionsCount }: AiChatbotProps) => {
  return (
    <AiChatbotProvider questionsCount={questionsCount}>
      <AiChatbotContent />
      <EditChatNameModal />
    </AiChatbotProvider>
  );
};

const Logo = () => {
  if (isUserLoggedInWithCustomUrl() || is3asApp()) {
    return (
      <div className='text-white text-display-small leading-display-small w-full h-full flex items-center justify-center'>
        AI
      </div>
    );
  }

  return <TwiplaLogo color='#FFF' className='w-12u h-12u' />;
};
